import { useEffect, useRef, useState } from "react";
import { itemById } from "@zend/content";
import { useT } from "../i18n/index.js";
import { useSession } from "../state/session.js";
import { Button } from "../ui/Button.js";
import { formatCompact } from "../ui/Counter.js";
import { errorText } from "../base/state.js";
import { unitsApi } from "./api.js";
import { useUnits } from "./state.js";
import { UnitStatsComparison } from "./UnitStatsComparison.js";
import { GameIcon, StatIcon } from "../assets/gameIcons.js";
import { xpStock } from "../inventory/stock.js";
import { getLocale } from "../i18n/index.js";
import { Link } from "../router.js";
import type { XpBatchRequest, XpPreview } from "./progression-types.js";
import type { UnitView } from "./types.js";
import { useReducedMotion } from "./useReducedMotion.js";
import "./progression.css";

export function UnitXpPanel({ unit, hq }: { unit: UnitView; hq: number }) {
  const reducedMotion = useReducedMotion();
  const t = useT(), inventory = useSession((state) => state.inventory), run = useUnits((state) => state.run);
  const [draft, setDraft] = useState<Record<string, number>>({});
  const [preview, setPreview] = useState<XpPreview | null>(null), [error, setError] = useState("");
  const [loading, setLoading] = useState(false), [saving, setSaving] = useState(false), [revision, setRevision] = useState(0);
  const [result, setResult] = useState<XpPreview | null>(null);
  const resultElement = useRef<HTMLDivElement | null>(null);
  const inFlight = useRef(false), alive = useRef(true), sequence = useRef(0);
  useEffect(() => { alive.current = true; return () => { alive.current = false; sequence.current++; }; }, []);
  const stock = inventory.filter((item) => item.itemId.startsWith("itm.xp_") && item.qty > 0).sort((a, b) => Number(itemById(a.itemId).params["xp"]) - Number(itemById(b.itemId).params["xp"]));
  const request: XpBatchRequest = { mode: "manual", items: Object.entries(draft).filter(([, qty]) => qty > 0).map(([itemId, qty]) => ({ itemId, qty })) };
  const requestKey = JSON.stringify(request), stockKey = JSON.stringify(stock);
  useEffect(() => {
    const current = ++sequence.current;
    setPreview(null); setError(""); setLoading(true);
    const timer = setTimeout(() => {
      void unitsApi.xpPreview(unit.id, JSON.parse(requestKey) as XpBatchRequest).then((next) => { if (current === sequence.current) setPreview(next); })
        .catch((e: unknown) => { if (current === sequence.current) setError(errorText(e)); })
        .finally(() => { if (current === sequence.current) setLoading(false); });
    }, 180);
    return () => { clearTimeout(timer); sequence.current++; };
  }, [unit.id, unit.level, unit.xp, unit.stars, unit.levelCap, unit.power, requestKey, stockKey, revision]);
  useEffect(() => {
    if (!result) return;
    resultElement.current?.scrollIntoView({ block: "center", behavior: "auto" });
    const timer = setTimeout(() => setResult(null), 4500);
    return () => clearTimeout(timer);
  }, [result]);
  const max = async () => {
    if (inFlight.current) return;
    inFlight.current = true; setLoading(true); setError(""); sequence.current++;
    try {
      const next = await unitsApi.xpPreview(unit.id, { mode: "max", items: [] });
      if (alive.current) { setDraft(Object.fromEntries(next.items.map((item) => [item.itemId, item.qty]))); setRevision((n) => n + 1); }
    } catch (e) { if (alive.current) setError(errorText(e)); }
    finally { inFlight.current = false; if (alive.current) setLoading(false); }
  };
  const apply = async () => {
    if (!preview?.canApply || inFlight.current) return;
    inFlight.current = true; setSaving(true);
    try {
      const applied = await run(() => unitsApi.xpBatch(unit.id, request, preview.quoteToken));
      if (!alive.current) return;
      if (applied) {
        useUnits.setState((state) => ({ roster: state.roster ? { ...state.roster, units: state.roster.units.map((row) => row.id === applied.after.id ? applied.after : row) } : null }));
        setResult(applied); setDraft({});
      }
      setRevision((n) => n + 1);
    } finally { inFlight.current = false; if (alive.current) setSaving(false); }
  };
  const capped = unit.level >= unit.levelCap;
  const available = xpStock(inventory);
  return <section className="section xp-planner" data-testid="unit-xp">
    <div className="xp-stock-summary" data-testid="xp-stock-summary"><GameIcon id="itm.xp_10000" size={44} /><div><small>{t("inventory.availableXp")}</small><strong>{available.xp.toLocaleString(getLocale())} EXP</strong><span>{t("inventory.xpBoxes", { n: available.boxes.toLocaleString(getLocale()) })}</span></div><Link to="/app/inventory">{t("inventory.title")}</Link></div>
    <h3>{t("units.level")} {unit.level} / {unit.levelCap}</h3>
    <div className="progress"><div className="progress-fill" style={{ width: capped ? "100%" : `${Math.min(100, unit.xp / Math.max(1, unit.xp + unit.xpToNext) * 100)}%` }} /></div>
    <p className="hint">{capped ? t("units.levelCapHint", { stars: unit.stars, hq }) : t("units.xpToNext", { n: formatCompact(unit.xpToNext) })}</p>
    <p>{t("units.xp.reviewHint")}</p>
    {stock.map((item) => <label className="xp-selection" key={item.itemId}>
      <span><GameIcon id={item.itemId} size={36} /> {Number(itemById(item.itemId).params["xp"]).toLocaleString(getLocale())} EXP <small>×{item.qty.toLocaleString(getLocale())}</small></span>
      <input type="range" min={0} max={Math.min(item.qty, 1_000_000)} value={draft[item.itemId] ?? 0} disabled={capped || saving || loading && inFlight.current} onChange={(event) => setDraft({ ...draft, [item.itemId]: Number(event.target.value) })} aria-label={t("units.xp.quantity", { n: Number(itemById(item.itemId).params["xp"]) })} data-testid={`xp-${item.itemId}`} />
      <output>{draft[item.itemId] ?? 0}</output>
    </label>)}
    {!stock.length ? <p className="hint">{t("units.xp.none")}</p> : null}
    <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
      <Button onClick={() => void max()} disabled={capped || saving || loading} data-testid="xp-max">{t("units.xp.max")}</Button>
      <Button onClick={() => { setDraft({}); setResult(null); }} disabled={saving} data-testid="xp-reset">{t("units.xp.reset")}</Button>
    </div>
    {error ? <p role="alert">{error}</p> : null}
    {loading ? <p role="status">{t("units.xp.calculating")}</p> : preview ? <div className="xp-quote" data-testid="xp-preview">
      <strong>{t("units.xp.preview", { from: preview.before.level, to: preview.after.level, xp: formatCompact(preview.gainedXp) })}</strong>
      <p className="hint">{preview.items.map((item) => `${item.qty} × ${formatCompact(item.xpEach)} XP`).join(" + ") || t("units.xp.noSelection")}</p>
      <p className="hint">{t("units.xp.bonus", { n: preview.bonusPct })}</p>
      {preview.after.level >= preview.after.levelCap && preview.after.xp > 0 ? <p className="hint">{t("units.xp.banked", { n: formatCompact(preview.after.xp) })}</p> : null}
      {preview.levelsGained > 0 ? <UnitStatsComparison before={preview.before} after={preview.after} /> : null}
      <Button variant="primary" loading={saving} disabled={!preview.canApply} onClick={() => void apply()} data-testid="xp-apply">{t("units.xp.apply")}</Button>
    </div> : null}
    {result ? <div ref={resultElement} key={`${result.after.level}-${result.after.xp}`} className={`xp-level-burst ${result.milestones.length ? "milestone" : ""}`} style={reducedMotion ? { animation: "none" } : undefined} role="status" data-testid="xp-level-result" data-level={result.after.level}>
      <strong>{t(result.milestones.length ? "units.xp.milestone" : result.levelsGained ? "units.xp.levelUp" : "units.xp.applied", { n: result.after.level, xp: formatCompact(result.gainedXp) })}</strong>
      {result.milestones.length ? <span className="row" style={{ gap: 8 }}>{result.milestones.map((level) => <span key={level}><StatIcon stat="star" /> {level}</span>)}</span> : null}
    </div> : null}
  </section>;
}
